import { CalendarItem } from '@wui/common-library'
import dayjs from 'dayjs'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { DEFAULT_TIMEZONE } from '@/features/timezone-selector/constants'

import { useCalendarStore } from '@/entities/calendar'
import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { useLocalization } from '@/shared/composables/localization'
import { useStoreConfiguration } from '@/shared/store/configuration'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'

import { AlarmSchedule, PostAlarmFile, PromptAlarm } from '../types'

export const useStoreAlarm = defineStore('alarm', () => {
  const { t, currentLocale } = useLocalization()
  const alarmSchedules = ref(new Map<string, AlarmSchedule>())
  const isLoading = ref(false)
  const _loaderMap = ref(new Map<string, { isSaving: boolean, isDeleting: boolean }>())
  const promptAlarms = ref(new Map<string, PromptAlarm>())
  const promptCategories = ref<Array<{ label: string, value: string }>>([{ label: t('common.all'), value: 'All' }])
  const _standardAlarm = ref<PromptAlarm>()
  const lang = ref(currentLocale)

  const defaultAlarm = computed(() => _standardAlarm.value ?? {} as PromptAlarm)
  const _loadersByGuid = (guid: string) => _loaderMap.value.get(guid)
  const getAlarmByGuid = (guid: string) => alarmSchedules.value.get(guid)

  const _setAlarmSchedule = (alarmSchedule: AlarmSchedule) => {
    alarmSchedules.value.set(alarmSchedule.guid, alarmSchedule)
    _loaderMap.value.set(alarmSchedule.guid, { isDeleting: false, isSaving: false })
  }

  const setPromptAlarms = (prompts: PromptAlarm[]) => {
    prompts.forEach((prompt) => {
      promptAlarms.value.set(prompt.guid, prompt)
    })
  }

  const _deleteAlarmSchedule = (guid: string) => {
    if (alarmSchedules.value.has(guid)) {
      alarmSchedules.value.delete(guid)
    }
    if (_loaderMap.value.has(guid)) {
      _loaderMap.value.delete(guid)
    }
  }

  const findPromptAlarms = (value: string): Map<string, PromptAlarm> => {
    if (value === 'All') {
      return promptAlarms.value
    } else {
      const alarm = promptAlarms.value.get(value)
      if (!alarm) return promptAlarms.value
      const map = new Map()
      map.set(alarm.guid, alarm)
      return map
    }
  }

  const fetchAlarmSchedules = (): Promise<AlarmSchedule[]> => {
    return new Promise((resolve, reject) => {
      const fetch = useFetchApi()
      isLoading.value = true
      alarmSchedules.value.clear()
      fetch.get<AlarmSchedule[]>(getAppURL({ pathName: '/api/alarm' }))
        .then((response: { status: number; data: AlarmSchedule[] }) => {
          if (response.status === 200) {
            const schedules = response.data
            schedules.forEach((schedule) => {
              if (typeof schedule.date === 'string') {
                schedule.date = schedule.date.substring(0, 10)
              }
              _setAlarmSchedule(schedule)
            })
            resolve(schedules)
          } else {
            reject(new Error(t('common.errorWhileRequest', { err: response.status })))
          }
        }).catch((e: unknown) => {
          reject(e)
        }).finally(() => {
          isLoading.value = false
        })
    })
  }

  const _updateAlarmSchedule = (alarmSchedule: AlarmSchedule): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const fetch = useFetchApi()
      fetch.put<void>(getAppURL({ pathName: '/api/alarm' }), alarmSchedule)
        .then((response: { status: number }) => {
          if (response.status === 204) {
            _setAlarmSchedule(alarmSchedule)
            resolve(true)
          } else {
            reject(new Error(t('common.errorWhileRequest', { err: response.status })))
          }
        }).catch((e: unknown) => {
          reject(e)
        }).finally(() => {
          isLoading.value = false
        })
    })
  }

  const _createAlarmSchedule = (alarmSchedule: AlarmSchedule): Promise<AlarmSchedule> => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const fetch = useFetchApi()
      fetch.post<AlarmSchedule>(getAppURL({ pathName: '/api/alarm' }), alarmSchedule)
        .then((response: { status: number; data: AlarmSchedule }) => {
          if (response.status === 201) {
            const schedule = response.data
            if (typeof schedule.date === 'string') {
              schedule.date = dayjs(schedule.date).tz().format('YYYY-MM-DD')
            }
            _setAlarmSchedule(schedule)
            resolve(schedule)
          } else {
            reject(new Error(t('common.errorWhileRequest', { err: response.status })))
          }
        }).catch((e: unknown) => {
          reject(e)
        }).finally(() => {
          isLoading.value = false
        })
    })
  }

  const saveAlarmSchedule = (alarmSchedule: AlarmSchedule): Promise<AlarmSchedule | boolean> => {
    return new Promise((resolve, reject) => {
      const loaders = _loadersByGuid(alarmSchedule.guid)
      if (loaders) loaders.isSaving = true
      if (alarmSchedule.isNew) {
        _createAlarmSchedule(alarmSchedule)
          .then((schedule) => {
            resolve(schedule)
            _deleteAlarmSchedule(alarmSchedule.guid)
          }).catch((e: unknown) => {
            reject(e)
          }).finally(() => {
            if (loaders) loaders.isSaving = false
          })
      } else {
        _updateAlarmSchedule(alarmSchedule)
          .then((data) => {
            resolve(data)
          }).catch((e: unknown) => {
            reject(e)
          }).finally(() => {
            if (loaders) loaders.isSaving = false
          })
      }
    })
  }

  const removeAlarmSchedule = (alarmSchedule: AlarmSchedule): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const loaders = _loadersByGuid(alarmSchedule.guid)
      if (loaders) loaders.isDeleting = true
      const guid = alarmSchedule.guid
      if (alarmSchedule.isNew) {
        _deleteAlarmSchedule(guid)
        resolve(true)
        isLoading.value = false
        if (loaders) loaders.isDeleting = false
      } else {
        const fetch = useFetchApi()
        fetch.delete<void>(getAppURL({ pathName: `/api/alarm/${guid}` })).then((response: { status: number }) => {
          if (response.status === 204) {
            resolve(true)
            _deleteAlarmSchedule(guid)
          } else {
            reject(new Error(t('common.errorWhileRequest', { err: response.status })))
          }
        }).catch((e: unknown) => {
          reject(e)
        }).finally(() => {
          isLoading.value = false
          if (loaders) loaders.isDeleting = false
        })
      }
    })
  }

  const fetchPromptAlarmStandard = (): Promise<PromptAlarm> => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const fetch = useFetchApi()
      fetch.get<PromptAlarm>(getAppURL({ pathName: '/api/prompt-alarm/standard' }), {
        params: {
          lang: lang.value,
        },
      }).then((response: { status: number; data: PromptAlarm }) => {
        if (response.status === 200) {
          updateStandardAlarm(response.data)

          resolve(response.data)
        } else {
          reject(new Error(t('common.errorWhileRequest', { err: response.status })))
        }
      }).catch((e: unknown) => {
        reject(e)
      }).finally(() => {
        isLoading.value = false
      })
    })
  }

  const fetchPromptAlarmSubscriber = (): Promise<PromptAlarm[]> => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const fetch = useFetchApi()
      fetch.get<PromptAlarm[]>(getAppURL({ pathName: '/api/prompt-alarm/subscriber' }), {
        params: {
          lang: lang.value,
        },
      }).then((response: { status: number; data: PromptAlarm[] }) => {
        if (response.status === 200) {
          resolve(response.data)
          setPromptAlarms(response.data)
        } else {
          reject(new Error(t('common.errorWhileRequest', { err: response.status })))
        }
      }).catch((e: unknown) => {
        reject(e)
      }).finally(() => {
        isLoading.value = false
      })
    })
  }

  const createPromptAlarm = (postAlarmFile: PostAlarmFile): Promise<PromptAlarm> => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const formData = new FormData()
      formData.append('file', postAlarmFile.file)
      const fetch = useFetchApi()
      fetch.post<PromptAlarm>(getAppURL({ pathName: '/api/prompt' }), formData, {
        params: {
          categoryName: postAlarmFile.categoryName,
          promptName: postAlarmFile.promptName,
          lang: lang.value,
        },
      }).then((response: { status: number; data: PromptAlarm | PromiseLike<PromptAlarm> }) => {
        if (response.status === 201) {
          resolve(response.data)
        } else {
          reject(new Error(t('common.errorWhileRequest', { err: response.status })))
        }
      }).catch((e: unknown) => {
        reject(e)
      }).finally(() => {
        isLoading.value = false
      })
    })
  }

  const removePromptByGuid = (guid: string) => {
    return new Promise((resolve, reject) => {
      isLoading.value = true
      const fetch = useFetchApi()
      fetch.delete(getAppURL({ pathName: `/api/prompt/${guid}` })).then((response: { status: number }) => {
        if (response.status === 204) {
          resolve(true)
        } else {
          reject(new Error(t('common.errorWhileRequest', { err: response.status })))
        }
      }).catch((e: unknown) => {
        reject(e)
      }).finally(() => {
        isLoading.value = false
      })
    })
  }

  const updateStandardAlarm = (prompt: PromptAlarm) => {
    _standardAlarm.value = prompt
  }

  const setPromptCategories = (prompts: PromptAlarm[]) => {
    prompts.forEach((prompt) => {
      promptCategories.value.push({ label: prompt.guid, value: prompt.guid })
    })
  }

  const { selectedYear, selectedMonth } = storeToRefs(useCalendarStore())
  const { configuration } = storeToRefs(useStoreConfiguration())
  const { preferences } = storeToRefs(usePreferencesStore())
  // Отображение в календаре — в зоне пользователя (с сервера приходит время в зоне tzOffset).
  const selectedTimezone = computed<string>(() => {
    const tz = preferences.value?.[PreferenceKey.CURRENT_TIME_ZONE] ?? DEFAULT_TIMEZONE
    return typeof tz === 'string' ? tz : DEFAULT_TIMEZONE
  })

  const alarmCalendarItems = computed<CalendarItem[]>(() => {
    const firstDayOfSelectedMonth = dayjs(`${+selectedYear.value}-${+selectedMonth.value + 1}-01`)
    // dayjs().day(): 0 = Sunday, 1 = Monday, 2 = Tuesday, ... 6 = Saturday
    const alarmsByWeekdays: AlarmSchedule[][] = [
      [], // [0] sunday
      [], // [1] monday
      [], // [2] tuesday
      [], // [3] wednesday
      [], // [4] thursday
      [], // [5] friday
      [], // [6] saturday
    ]
    const calendarItems: CalendarItem[] = []

    Array.from(alarmSchedules.value.values()).forEach(alarm => {
      if (!alarm.enabled) {
        return
      }

      if (!alarm.date) {
        if (alarm.sunday) alarmsByWeekdays[0]?.push(alarm)
        if (alarm.monday) alarmsByWeekdays[1]?.push(alarm)
        if (alarm.tuesday) alarmsByWeekdays[2]?.push(alarm)
        if (alarm.wednesday) alarmsByWeekdays[3]?.push(alarm)
        if (alarm.thursday) alarmsByWeekdays[4]?.push(alarm)
        if (alarm.friday) alarmsByWeekdays[5]?.push(alarm)
        if (alarm.saturday) alarmsByWeekdays[6]?.push(alarm)
        return
      }

      const alarmDate = dayjs(`${alarm.date} ${alarm.time}`).tz('utc', true).subtract(configuration.value.tzOffset, 'minutes').tz(selectedTimezone.value)
      if (alarmDate.year() === +selectedYear.value && alarmDate.month() === +selectedMonth.value) {
        calendarItems.push({
          id: alarm.guid,
          timestamp: alarmDate.unix(),
          description: alarm.name,
          type: 'primary',
          icon: 'notifications',
        })
      }
    })

    for (let i = 1; i <= firstDayOfSelectedMonth.daysInMonth(); i++) {
      const day = firstDayOfSelectedMonth.set('date', i).day()
      alarmsByWeekdays[day]?.forEach(alarm => {
        const [hour, minute] = alarm.time.split(':').map(Number)
        calendarItems.push({
          id: alarm.guid,
          timestamp: firstDayOfSelectedMonth.set('date', i).set('hour', hour ?? 0).set('minute', minute ?? 0).tz('utc', true).subtract(configuration.value.tzOffset, 'minutes').unix(),
          description: alarm.name,
          type: 'accent',
          icon: 'notifications',
        })
      })
    }

    return calendarItems
  })

  return {
    alarmSchedules,
    isLoading,
    promptAlarms,
    promptCategories,
    lang,
    setPromptAlarms,
    findPromptAlarms,
    fetchAlarmSchedules,
    saveAlarmSchedule,
    removeAlarmSchedule,
    fetchPromptAlarmStandard,
    fetchPromptAlarmSubscriber,
    defaultAlarm,
    getAlarmByGuid,
    createPromptAlarm,
    removePromptByGuid,
    updateStandardAlarm,
    setPromptCategories,
    alarmCalendarItems,
    selectedTimezone,
  }
})
